import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Stepper, Step, StepLabel,
  Paper, Grid, FormControlLabel, Checkbox, TextField,
  Rating, RadioGroup, Radio, FormControl, FormLabel,
  Alert, CircularProgress, Divider, Chip,
} from '@mui/material';
import {
  CheckCircle as DoneIcon,
  ArrowForward as NextIcon,
  ArrowBack as PrevIcon,
} from '@mui/icons-material';
import { api, apiRoutes } from '../../api';

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const STEPS = ['تأیید اطلاعات', 'امکانات تخصصی', 'نتیجه‌گیری'];

// ── Step 1: Verification with property context ────────────────────────────────
function VerificationStep({ data, onChange, property }) {
  const p = property || {};

  const row = (field, label, contextValue) => (
    <Grid item xs={12} key={field}>
      <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControlLabel
          sx={{ minWidth: 220, m: 0 }}
          control={
            <Checkbox
              checked={data[field]}
              onChange={e => onChange(field, e.target.checked)}
            />
          }
          label={label}
        />
        {contextValue && (
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {contextValue}
          </Typography>
        )}
      </Paper>
    </Grid>
  );

  const coords = p.latitude && p.longitude
    ? `${Number(p.latitude).toFixed(6)}, ${Number(p.longitude).toFixed(6)}`
    : null;

  const areaText = p.area ? `${p.area} متر مربع (اعلام‌شده توسط مالک)` : null;

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        اطلاعات زیر را با وضعیت واقعی ملک مقایسه کنید
      </Typography>
      <Grid container spacing={1}>
        {row('address_verified', 'آدرس تأیید شد', p.address)}
        {row('location_pin_verified', 'موقعیت روی نقشه تأیید شد', coords)}
        {row('layout_matches_description', 'نقشه با توضیحات مطابقت دارد', p.description)}
        {row('amenities_match_listing', 'امکانات با آگهی مطابقت دارد', null)}
        {row('photos_match_property', 'عکس‌ها با ملک مطابقت دارد', null)}
        {row('area_within_tolerance', 'متراژ در محدوده قابل قبول است', areaText)}
        <Grid item xs={12}>
          <TextField
            label="اختلاف متراژ واقعی با اعلامی (متر مربع)"
            type="number"
            size="small"
            value={data.area_difference_m2}
            onChange={e => onChange('area_difference_m2', e.target.value)}
            sx={{ width: 280 }}
            helperText="مثبت = بیشتر از اعلامی، منفی = کمتر از اعلامی"
          />
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Step 2: Agent Amenities ───────────────────────────────────────────────────
function AmenitiesStep({ propertyTypeId, amenityValues, amenityNotes, onValueChange, onNoteChange }) {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!propertyTypeId) return;
    setLoading(true);
    api
      .get(apiRoutes.properties.amenities, {
        params: { panel: 'agent_wizard', property_type: propertyTypeId },
        headers: authHeader(),
      })
      .then(res => setAmenities(res.data || []))
      .catch(() => setAmenities([]))
      .finally(() => setLoading(false));
  }, [propertyTypeId]);

  if (!propertyTypeId) {
    return <Alert severity="warning">نوع ملک در اطلاعات وظیفه یافت نشد.</Alert>;
  }

  if (loading) {
    return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  }

  if (amenities.length === 0) {
    return <Alert severity="info">امکانات تخصصی برای این نوع ملک تعریف نشده است.</Alert>;
  }

  const grouped = amenities.reduce((acc, a) => {
    (acc[a.value_type] = acc[a.value_type] || []).push(a);
    return acc;
  }, {});

  const typeLabel = { boolean: 'بله/خیر', numerical: 'عددی', categorical: 'طبقه‌بندی', text: 'متنی' };

  const renderInput = (amenity) => {
    const id = amenity.id;
    const val = amenityValues[id];

    switch (amenity.value_type) {
      case 'boolean':
        return (
          <Checkbox
            checked={val === true}
            onChange={e => onValueChange(id, e.target.checked)}
          />
        );
      case 'numerical':
        return (
          <TextField
            type="number"
            size="small"
            value={val ?? ''}
            inputProps={{ min: amenity.min_value, max: amenity.max_value, step: 0.1 }}
            onChange={e => onValueChange(id, e.target.value === '' ? null : parseFloat(e.target.value))}
            sx={{ width: 110 }}
            placeholder="مقدار"
          />
        );
      case 'categorical': {
        const opts = amenity.categorical_options || {};
        if (Object.keys(opts).length > 0) {
          return (
            <TextField
              select size="small"
              value={val ?? ''}
              onChange={e => onValueChange(id, e.target.value === '' ? null : parseInt(e.target.value))}
              sx={{ width: 150 }}
              SelectProps={{ native: true }}
            >
              <option value="">انتخاب</option>
              {Object.entries(opts).map(([k, lbl]) => (
                <option key={k} value={k}>{lbl}</option>
              ))}
            </TextField>
          );
        }
        return (
          <TextField
            type="number" size="small"
            value={val ?? ''}
            inputProps={{ min: amenity.min_value ?? 1, max: amenity.max_value ?? 5 }}
            onChange={e => onValueChange(id, e.target.value === '' ? null : parseInt(e.target.value))}
            sx={{ width: 100 }}
            placeholder="امتیاز"
          />
        );
      }
      case 'text':
        return (
          <TextField
            size="small" value={val ?? ''}
            onChange={e => onValueChange(id, e.target.value || null)}
            sx={{ width: 180 }}
            placeholder="توضیحات"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>امکانات تخصصی (ارزیابی کارشناس)</Typography>
      {Object.entries(grouped).map(([type, items]) => (
        <Paper key={type} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Chip label={typeLabel[type] || type} size="small" sx={{ mb: 1.5 }} />
          {items.map(amenity => (
            <Box key={amenity.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ minWidth: 160, flex: 1 }}>
                {amenity.description || amenity.name}
              </Typography>
              {renderInput(amenity)}
              {amenity.value_type !== 'boolean' && (
                <TextField
                  size="small"
                  value={amenityNotes[amenity.id] || ''}
                  onChange={e => onNoteChange(amenity.id, e.target.value)}
                  placeholder="یادداشت"
                  sx={{ width: 150 }}
                />
              )}
            </Box>
          ))}
        </Paper>
      ))}
    </Box>
  );
}

// ── Step 3: Result & Notes ────────────────────────────────────────────────────
function ResultStep({ data, onChange }) {
  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>ارزیابی کلی و نتیجه</Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>دقت آگهی (۱ تا ۵)</Typography>
        <Rating
          value={data.listing_accuracy_rating}
          onChange={(_, v) => onChange('listing_accuracy_rating', v)}
        />
        <Box mt={1.5}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.has_critical_discrepancy}
                onChange={e => onChange('has_critical_discrepancy', e.target.checked)}
              />
            }
            label="مغایرت حیاتی وجود دارد"
          />
        </Box>
        {data.has_critical_discrepancy && (
          <TextField
            label="خلاصه مغایرت‌ها" fullWidth multiline rows={2} size="small" sx={{ mt: 1 }}
            value={data.discrepancies_summary}
            onChange={e => onChange('discrepancies_summary', e.target.value)}
          />
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <FormControl>
          <FormLabel>نتیجه بازرسی</FormLabel>
          <RadioGroup row value={data.result} onChange={e => onChange('result', e.target.value)}>
            <FormControlLabel value="PASS" control={<Radio color="success" />} label="تأیید" />
            <FormControlLabel value="CHANGES_REQUESTED" control={<Radio color="warning" />} label="نیاز به اصلاح" />
            <FormControlLabel value="FAIL" control={<Radio color="error" />} label="رد" />
          </RadioGroup>
        </FormControl>
        <Box mt={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.requires_owner_changes}
                onChange={e => onChange('requires_owner_changes', e.target.checked)}
              />
            }
            label="مالک باید تغییراتی اعمال کند"
          />
        </Box>
        {data.requires_owner_changes && (
          <TextField
            label="درخواست‌های تغییر برای مالک" fullWidth multiline rows={2} size="small" sx={{ mt: 1 }}
            value={data.owner_change_requests}
            onChange={e => onChange('owner_change_requests', e.target.value)}
          />
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>یادداشت‌ها</Typography>
        <TextField
          label="یادداشت داخلی (فقط برای کارشناسان)" fullWidth multiline rows={2} size="small" sx={{ mb: 1.5 }}
          value={data.agent_internal_notes}
          onChange={e => onChange('agent_internal_notes', e.target.value)}
        />
        <TextField
          label="خلاصه قابل نمایش به مالک" fullWidth multiline rows={2} size="small"
          value={data.owner_visible_summary}
          onChange={e => onChange('owner_visible_summary', e.target.value)}
        />
      </Paper>
    </Box>
  );
}

// ── Main InspectionWizard ─────────────────────────────────────────────────────
export default function InspectionWizard({ taskId, onSubmitted }) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(true);
  const [error, setError] = useState(null);
  const [property, setProperty] = useState(null);
  const [propertyTypeId, setPropertyTypeId] = useState(null);

  // Fetch fresh task data so we always have property_type
  useEffect(() => {
    setTaskLoading(true);
    api
      .get(apiRoutes.inspectionTasks.detail(taskId), { headers: authHeader() })
      .then(res => {
        const prop = res.data?.property || {};
        setProperty(prop);
        // property_type may be a raw int FK or a nested object
        const ptId = prop.property_type?.id ?? prop.property_type ?? null;
        setPropertyTypeId(ptId);
      })
      .catch(() => setError('خطا در بارگذاری اطلاعات وظیفه'))
      .finally(() => setTaskLoading(false));
  }, [taskId]);

  const [verification, setVerification] = useState({
    address_verified: false,
    location_pin_verified: false,
    layout_matches_description: false,
    amenities_match_listing: false,
    photos_match_property: false,
    area_within_tolerance: true,
    area_difference_m2: '',
  });

  const [amenityValues, setAmenityValues] = useState({});
  const [amenityNotes, setAmenityNotes] = useState({});

  const [result, setResult] = useState({
    listing_accuracy_rating: 3,
    has_critical_discrepancy: false,
    discrepancies_summary: '',
    result: 'PASS',
    requires_owner_changes: false,
    owner_change_requests: '',
    agent_internal_notes: '',
    owner_visible_summary: '',
  });

  const setVerificationField = (field, value) =>
    setVerification(prev => ({ ...prev, [field]: value }));

  const setResultField = (field, value) =>
    setResult(prev => ({ ...prev, [field]: value }));

  const step3Valid = !(result.result === 'PASS' && result.has_critical_discrepancy);

  const handleSubmit = async () => {
    if (!step3Valid) {
      setError('نمی‌توان با وجود مغایرت حیاتی نتیجه «تأیید» انتخاب کرد.');
      return;
    }
    setLoading(true);
    setError(null);

    const amenities = Object.keys(amenityValues)
      .filter(id => amenityValues[id] !== null && amenityValues[id] !== undefined && amenityValues[id] !== '')
      .map(id => ({
        amenity: parseInt(id),
        value: amenityValues[id],
        notes: amenityNotes[id] || '',
      }));

    const payload = {
      ...verification,
      area_difference_m2: verification.area_difference_m2 !== '' ? parseInt(verification.area_difference_m2) : null,
      amenities,
      ...result,
    };

    try {
      await api.post(
        apiRoutes.inspectionTasks.inspection(taskId),
        payload,
        { headers: authHeader() }
      );
      onSubmitted();
    } catch (err) {
      const data = err.response?.data;
      setError(typeof data === 'object' ? JSON.stringify(data) : data || 'خطا در ثبت بازرسی');
    } finally {
      setLoading(false);
    }
  };

  if (taskLoading) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  return (
    <Box dir="rtl" className="agents-section agents-inspection-wizard">
      <Typography variant="h6" gutterBottom>فرم بازرسی</Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ minHeight: 300 }}>
        {activeStep === 0 && (
          <VerificationStep
            data={verification}
            onChange={setVerificationField}
            property={property}
          />
        )}
        {activeStep === 1 && (
          <AmenitiesStep
            propertyTypeId={propertyTypeId}
            amenityValues={amenityValues}
            amenityNotes={amenityNotes}
            onValueChange={(id, val) => setAmenityValues(prev => ({ ...prev, [id]: val }))}
            onNoteChange={(id, note) => setAmenityNotes(prev => ({ ...prev, [id]: note }))}
          />
        )}
        {activeStep === 2 && (
          <ResultStep data={result} onChange={setResultField} />
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box display="flex" justifyContent="space-between">
        <Button
          startIcon={<PrevIcon />}
          onClick={() => setActiveStep(s => s - 1)}
          disabled={activeStep === 0}
        >
          مرحله قبل
        </Button>

        {activeStep < STEPS.length - 1 ? (
          <Button
            variant="contained"
            endIcon={<NextIcon />}
            onClick={() => setActiveStep(s => s + 1)}
          >
            مرحله بعد
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={18} /> : <DoneIcon />}
            onClick={handleSubmit}
            disabled={loading || !step3Valid}
          >
            ثبت نهایی بازرسی
          </Button>
        )}
      </Box>
    </Box>
  );
}
